import React from 'react';
import { FolderOpen, CheckCircle2, AlertCircle } from 'lucide-react';
import { ProgressBarTrack, ProgressBarFill } from '../uploadDocuments.styles';

// Computes aggregate stats for one folder's worth of documents/items.
// Works with either shape as long as each entry has `status` and
// (optionally) `progress` — i.e. both the `documents` state in
// UploadDocuments.jsx and the `items` prop of UploadProgressModal.
export const getFolderStats = (docs) => {
  const total = docs.length;
  const done = docs.filter((d) => d.status === 'done').length;
  const skipped = docs.filter((d) => d.status === 'skipped').length;
  const errored = docs.filter((d) => d.status === 'error' || d.status === 'size-error').length;
  const settled = done + skipped + errored;
  const isUploading = docs.some((d) => d.status === 'uploading');
  const isComplete = total > 0 && settled === total;

  // Overall percent = average of each doc's own progress. Done counts as
  // 100 regardless of its last recorded `progress` value.
  const overallPercent =
    total === 0
      ? 0
      : Math.round(
          docs.reduce((sum, d) => sum + (d.status === 'done' ? 100 : (d.progress ?? 0)), 0) / total
        );

  return { total, done, skipped, errored, settled, isUploading, isComplete, overallPercent };
};

// rootFolder: display name for the folder
// docs: array of { status, progress } for every file that belongs to it
const FolderProgressBar = ({ rootFolder, docs, style }) => {
  const { total, done, errored, isUploading, isComplete, overallPercent } = getFolderStats(docs);

  if (total === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 180,
        flex: '1 1 220px',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: 'var(--color-text-tertiary)',
        }}
      >
        {isComplete && errored === 0 ? (
          <CheckCircle2 size={13} style={{ color: '#2f9e5b', flexShrink: 0 }} />
        ) : errored > 0 && isComplete ? (
          <AlertCircle size={13} style={{ color: '#e5484d', flexShrink: 0 }} />
        ) : (
          <FolderOpen size={13} style={{ flexShrink: 0 }} />
        )}
        <span
          title={rootFolder}
          style={{
            maxWidth: 130,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {rootFolder}
        </span>
        <span style={{ flexShrink: 0 }}>
          {done}/{total}
          {isUploading ? ` · ${overallPercent}%` : isComplete ? ' · done' : ''}
        </span>
      </div>
      <ProgressBarTrack aria-label={`Upload progress for folder ${rootFolder}`}>
        <ProgressBarFill style={{ width: `${overallPercent}%` }} />
      </ProgressBarTrack>
    </div>
  );
};

export default FolderProgressBar;
